// Store-credit ledger tab for Customer 360.

import { useMemo } from 'react';
import type { ColumnDef } from '@bbpos/ui';
import { Badge } from '@bbpos/ui';
import type { CurrencyCode } from '@bbpos/types';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { ReportTable } from '@/features/reports';
import type { StoreCreditLedgerRow } from '../types';
import { formatDateTime } from '../format';

type Row = StoreCreditLedgerRow;

const TYPE_VARIANT: Record<Row['type'], 'success' | 'info' | 'warning' | 'danger'> = {
  EARN: 'success',
  REDEEM: 'info',
  EXPIRED: 'danger',
  ADJUSTED: 'warning',
};

function makeColumns(currency: CurrencyCode): ColumnDef<Row>[] {
  return [
  {
    key: 'date',
    header: 'Date',
    render: (_v, row) => <span className="text-sm text-neutral-600 dark:text-neutral-300 tabular-nums">{formatDateTime(row.date)}</span>,
  },
  {
    key: 'type',
    header: 'Type',
    render: (_v, row) => <Badge variant={TYPE_VARIANT[row.type]} size="sm">{row.type}</Badge>,
  },
  {
    key: 'description',
    header: 'Description',
    render: (_v, row) => (
      <div>
        <p className="text-sm text-neutral-800 dark:text-neutral-200">{row.description ?? '—'}</p>
        {row.bill_number ? <p className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">{row.bill_number}</p> : null}
        {row.redemption_code ? (
          <p className="text-[11px] font-mono tabular-nums text-brand-700 dark:text-brand-300 mt-0.5">
            Code: {row.redemption_code}
          </p>
        ) : null}
      </div>
    ),
  },
  {
    key: 'amount',
    header: 'Amount',
    numeric: true,
    render: (_v, row) => {
      const sign = row.amount >= 0 ? '+' : '−';
      const cls = row.amount >= 0 ? 'text-success-600 dark:text-success-300' : 'text-danger-600 dark:text-danger-300';
      return <span className={`text-sm font-bold tabular-nums ${cls}`}>{sign}{formatPaise(Math.abs(row.amount), currency)}</span>;
    },
  },
  {
    key: 'balance_after',
    header: 'Balance',
    numeric: true,
    render: (_v, row) => <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">{formatPaise(row.balance_after, currency)}</span>,
  },
  ];
}

export interface CreditLedgerTableProps {
  rows: Row[];
  loading?: boolean;
}

export function CreditLedgerTable({ rows, loading }: CreditLedgerTableProps) {
  const currency = useTenantCurrency();
  const columns = useMemo(() => makeColumns(currency), [currency]);
  return (
    <ReportTable<Row>
      title="Store Credit Ledger"
      subtitle="Credit notes issued, redeemed, expired or manually adjusted."
      columns={columns}
      data={rows}
      loading={loading}
      totalCount={rows.length}
      emptyTitle="No store credit activity"
      emptyDescription="Credit notes and refunds that convert to store credit will appear here."
    />
  );
}
